/**
 * Adapter for the Mauvely design system bundle.
 *
 * `_ds_bundle.js` is a prebuilt IIFE that reads a bare `React` global and
 * assigns 18 components onto `window.MauvelyDesignSystem_227a0c`. The `.jsx`
 * sources it was compiled from were not part of the design handoff, so the
 * bundle is kept as the component layer rather than rewritten — reimplementing
 * 18 components blind would drift from a design that has already been signed off.
 *
 * Two consequences, both handled in `loadDesignSystem`:
 *
 *   · React must exist on `window` BEFORE the bundle runs. We publish the very
 *     same React instance the app imports, so there is exactly one copy and
 *     hooks work across the boundary. (Loading React separately from a CDN —
 *     which is what the design prototype did — would give two instances and
 *     break every hook in a DS component.)
 *   · The Icon component reads `window.lucide`. That comes from the bundled npm
 *     package here rather than unpkg, so the app has no CDN dependency.
 */

import React from 'react';
import * as ReactDOM from 'react-dom';
import { createElement as lucideCreateElement } from 'lucide';
import { iconSet } from './icons';

const BUNDLE_URL = '/_ds/mauvely/_ds_bundle.js';

type C<P = Record<string, unknown>> = React.ComponentType<P>;

export interface DesignSystem {
  Badge: C<{ tone?: string; children?: React.ReactNode; style?: React.CSSProperties }>;
  Button: C<{
    // The bundle's real variant list. Note there is NO `outline` — the design
    // prototype passed `variant="outline"` throughout, which fell through to the
    // `primary` default and rendered every secondary CTA as a filled violet
    // button. `secondary` (transparent, bordered) is what those call sites meant,
    // and is what restores the intended hierarchy on the pricing cards.
    variant?: 'primary' | 'secondary' | 'ghost' | 'soft' | 'signal' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    fullWidth?: boolean;
    disabled?: boolean;
    type?: 'button' | 'submit';
    onClick?: (e: React.MouseEvent) => void;
    children?: React.ReactNode;
    style?: React.CSSProperties;
  }>;
  Card: C<{
    as?: string;
    padding?: 'none' | 'compact' | 'md' | 'panel';
    tone?: 'surface' | 'sunken';
    interactive?: boolean;
    radius?: number | string;
    onClick?: (e: React.MouseEvent) => void;
    children?: React.ReactNode;
    style?: React.CSSProperties;
  }>;
  Icon: C<{ name: string; size?: number; strokeWidth?: number; color?: string; style?: React.CSSProperties }>;
  IconButton: C<Record<string, unknown>>;
  Stat: C<{ label?: string; value?: React.ReactNode; style?: React.CSSProperties }>;
  Tag: C<Record<string, unknown>>;
  Wordmark: C<Record<string, unknown>>;
  Dialog: C<Record<string, unknown>>;
  Progress: C<Record<string, unknown>>;
  Toast: C<Record<string, unknown>>;
  Tooltip: C<Record<string, unknown>>;
  Checkbox: C<{
    label?: string;
    description?: string;
    checked?: boolean;
    defaultChecked?: boolean;
    disabled?: boolean;
    // NOT a change event. The bundle renders a `<span role="checkbox">` rather
    // than an `<input>`, and calls `onChange(!on, e)` — the new value first.
    // This was declared as a ChangeEvent, so `e.target.checked` typechecked and
    // then threw at runtime, which is why the pin checkbox appeared dead.
    onChange?: (checked: boolean, e: React.SyntheticEvent) => void;
  }>;
  Input: C<{
    label?: string;
    type?: string;
    placeholder?: string;
    hint?: string;
    value?: string;
    defaultValue?: string;
    autoComplete?: string;
    multiline?: boolean;
    rows?: number;
    disabled?: boolean;
    invalid?: boolean;
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    style?: React.CSSProperties;
  }>;
  Radio: C<Record<string, unknown>>;
  Select: C<{
    label?: string;
    value?: string;
    options?: { value: string; label: string }[];
    onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    style?: React.CSSProperties;
  }>;
  Switch: C<{
    label?: string;
    description?: string;
    checked?: boolean;
    defaultChecked?: boolean;
    disabled?: boolean;
    /** Same shape as Checkbox above: `(nextValue, event)`, not a change event. */
    onChange?: (checked: boolean, e: React.SyntheticEvent) => void;
  }>;
  Tabs: C<Record<string, unknown>>;
  /** The bundle collects per-component init failures here instead of throwing. */
  __errors?: unknown[];
}

declare global {
  interface Window {
    React?: typeof React;
    ReactDOM?: typeof ReactDOM;
    lucide?: { icons: unknown; createElement: unknown };
    MauvelyDesignSystem_227a0c?: DesignSystem;
  }
}

let loaded: Promise<DesignSystem> | null = null;

export function loadDesignSystem(): Promise<DesignSystem> {
  if (loaded) return loaded;

  loaded = new Promise<DesignSystem>((resolve, reject) => {
    // Order matters: the bundle reads these at evaluation time.
    window.React = React;
    window.ReactDOM = ReactDOM;
    window.lucide = { icons: iconSet, createElement: lucideCreateElement };

    const script = document.createElement('script');
    script.src = BUNDLE_URL;
    script.async = false;

    script.onload = () => {
      const ns = window.MauvelyDesignSystem_227a0c;
      if (!ns) {
        reject(new Error('Design system bundle loaded but did not register its namespace.'));
        return;
      }
      // The bundle wraps each component in try/catch and pushes failures here.
      // Surface them — a silently missing component renders as nothing at all,
      // which is far harder to diagnose later.
      if (ns.__errors?.length) {
        console.error('Design system components failed to initialise:', ns.__errors);
      }
      resolve(ns);
    };

    script.onerror = () => reject(new Error(`Could not load ${BUNDLE_URL}`));
    document.head.appendChild(script);
  });

  return loaded;
}

/**
 * Synchronous accessor for render code. Safe because the app does not mount
 * until `loadDesignSystem()` resolves — see main.tsx.
 */
export function ds(): DesignSystem {
  const ns = window.MauvelyDesignSystem_227a0c;
  if (!ns) throw new Error('Design system used before it finished loading.');
  return ns;
}
